import React, { useState } from 'react';
import Yup from '~yup';
import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import { useAppDispatch, useAppSelector } from '~redux/hooks';

import { TDropdownOption } from '~types/components/labelledDropdown';

import * as FormStyles from './_createStockOutForm.module.scss';

const CreateStockOutForm = () => {
    const dispatch = useAppDispatch();

    const inventoryList = useAppSelector((state) => state.gold.allInventories);

    const [productName, setProductName] = useState('');
    const [availableAmount, setAvailableAmount] = useState<number | null>(null);

    const validationSchema = Yup.object().shape({
        productId: Yup.string().required('Select a product'),
        amount: Yup.number()
            .min(1, 'Amount must be at least 1')
            .typeError('Amount must be a number')
            .required('Amount is required'),
    });

    const formik = useFormik({
        initialValues: {
            productId: '',
            amount: '',
        },
        validationSchema,
        onSubmit: (_values) => {
            // TODO: wire up dispatch when saga is ready
        },
    });

    const productOptions: TDropdownOption[] = inventoryList.map((inventory) => ({
        label: inventory.productName,
        value: inventory.productId,
    }));

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProductName(event.target.value);

        const product = inventoryList.find((p) => p.productName === event.target.value);

        if (!product) {
            formik.setFieldValue('productId', '');
            setAvailableAmount(null);
            return;
        }

        formik.setFieldValue('productId', product.productId);
        setAvailableAmount(product.amount);
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        const product = inventoryList.find((p) => p.productId === option.value);

        if (!product) return;

        setProductName(product.productName);
        formik.setFieldValue('productId', product.productId);
        setAvailableAmount(product.amount);
    };

    return (
        <form onSubmit={formik.handleSubmit} className={FormStyles.container}>
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    id='productId'
                    name='productId'
                    label='Product Name* :'
                    options={productOptions.sort((a, b) => Number(a.value) - Number(b.value))}
                    value={productName}
                    error={formik.touched.productId ? formik.errors.productId : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!productOptions.find((p) => p.label === productName)}
                />

                <div className={FormStyles['available-balance']}>
                    <Text label='Available Amount:' />
                    <Text
                        label={availableAmount !== null ? `${availableAmount}` : '-'}
                        isBold
                    />
                </div>

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Amount* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />
            </div>

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        label='Create Stock Out'
                        type='submit'
                    />
                </div>
            </div>
        </form>
    );
};

export default CreateStockOutForm;
